/**
 * Utilidades para calcular horas hábiles en zona horaria MXN
 * Horas hábiles: Lunes a Viernes, 8:00 AM - 5:00 PM (hora México)
 * No cuenta sábados ni domingos
 */

const MXN_TIMEZONE = 'America/Mexico_City';
const BUSINESS_HOURS_START = 8; // 8:00 AM
const BUSINESS_HOURS_END = 17; // 5:00 PM

/**
 * Obtiene la fecha actual en zona horaria MXN
 */
function getMXNNow(): Date {
  return new Date(
    new Date().toLocaleString('en-US', {
      timeZone: MXN_TIMEZONE,
    })
  );
}

/**
 * Verifica si una fecha es día hábil (lunes a viernes)
 */
function isBusinessDay(date: Date): boolean {
  const dayOfWeek = date.getDay(); // 0 = Domingo, 6 = Sábado
  return dayOfWeek >= 1 && dayOfWeek <= 5; // Lunes (1) a Viernes (5)
}

/**
 * Avanza una fecha al siguiente día hábil (lunes a viernes)
 */
function nextBusinessDay(date: Date): Date {
  const next = new Date(date);
  next.setDate(next.getDate() + 1);

  // Si es sábado, avanzar a lunes
  if (next.getDay() === 6) {
    next.setDate(next.getDate() + 2);
  }
  // Si es domingo, avanzar a lunes
  else if (next.getDay() === 0) {
    next.setDate(next.getDate() + 1);
  }

  return next;
}

/**
 * Avanza una fecha al inicio del siguiente día hábil (9:00 AM)
 */
function nextBusinessDayStart(date: Date): Date {
  const next = nextBusinessDay(date);
  next.setHours(BUSINESS_HOURS_START, 0, 0, 0);
  return next;
}

/**
 * Calcula el límite de horas hábiles según el número de asientos
 * @param seatCount - Número de asientos en la compra
 * @returns Número de horas hábiles: 45 para <=10 asientos (5 días), 27 para >10 asientos (3 días)
 */
function getBusinessHoursLimit(seatCount: number): number {
  return seatCount <= 10 ? 45 : 27; // 5 días hábiles (45h) o 3 días hábiles (27h)
}

/**
 * Calcula la fecha/hora en que un pago pendiente será cancelado automáticamente
 * @param createdAt - Fecha de creación del pago
 * @param businessHoursLimit - Número de horas hábiles antes de cancelar (27 para >10 asientos, 45 para <=10 asientos)
 */
function calculateAutoCancelDate(createdAt: Date, businessHoursLimit = 48): Date {
  const startMXN = new Date(
    createdAt.toLocaleString('en-US', {
      timeZone: MXN_TIMEZONE,
    })
  );

  // Si la fecha de inicio es fin de semana, empezar desde el siguiente lunes 9 AM
  let current = isBusinessDay(startMXN) ? new Date(startMXN) : nextBusinessDayStart(startMXN);

  // Ajustar si está fuera del horario hábil
  if (isBusinessDay(current)) {
    const hour = current.getHours();
    if (hour < BUSINESS_HOURS_START) {
      current.setHours(BUSINESS_HOURS_START, 0, 0, 0);
    } else if (hour >= BUSINESS_HOURS_END) {
      current = nextBusinessDayStart(current);
    }
  }

  let hoursRemaining = businessHoursLimit;

  // Avanzar horas hábiles hasta completar el límite
  while (hoursRemaining > 0) {
    if (!isBusinessDay(current)) {
      current = nextBusinessDayStart(current);
      continue;
    }

    const dayStart = new Date(current);
    dayStart.setHours(BUSINESS_HOURS_START, 0, 0, 0);

    const dayEnd = new Date(current);
    dayEnd.setHours(BUSINESS_HOURS_END, 0, 0, 0);

    const hoursAvailableToday = (dayEnd.getTime() - current.getTime()) / (1000 * 60 * 60);

    if (hoursRemaining <= hoursAvailableToday) {
      // Se completa en el día actual
      current = new Date(current.getTime() + hoursRemaining * 60 * 60 * 1000);
      hoursRemaining = 0;
    } else {
      // Avanzar al siguiente día hábil
      hoursRemaining -= hoursAvailableToday;
      current = nextBusinessDayStart(current);
    }
  }

  return current;
}

/**
 * Calcula cuántas horas hábiles faltan hasta la cancelación automática
 * @param createdAt - Fecha de creación del pago
 * @param businessHoursLimit - Número de horas hábiles antes de cancelar (27 para >10 asientos, 45 para <=10 asientos)
 */
function calculateBusinessHoursRemaining(createdAt: Date, businessHoursLimit = 48): number {
  const autoCancelDate = calculateAutoCancelDate(createdAt, businessHoursLimit);
  const now = getMXNNow();

  if (now >= autoCancelDate) {
    return 0;
  }

  let current = new Date(now);
  let hoursRemaining = 0;

  // Si estamos en fin de semana, avanzar al siguiente lunes 9 AM
  if (!isBusinessDay(current)) {
    current = nextBusinessDayStart(current);
  } else {
    // Ajustar si está fuera del horario hábil
    const hour = current.getHours();
    if (hour < BUSINESS_HOURS_START) {
      current.setHours(BUSINESS_HOURS_START, 0, 0, 0);
    } else if (hour >= BUSINESS_HOURS_END) {
      current = nextBusinessDayStart(current);
    }
  }

  // Iterar día por día hasta llegar a autoCancelDate
  while (current < autoCancelDate) {
    if (isBusinessDay(current)) {
      const dayStart = new Date(current);
      dayStart.setHours(BUSINESS_HOURS_START, 0, 0, 0);

      const dayEnd = new Date(current);
      dayEnd.setHours(BUSINESS_HOURS_END, 0, 0, 0);

      // Si estamos en el mismo día que autoCancelDate
      if (
        current.getDate() === autoCancelDate.getDate() &&
        current.getMonth() === autoCancelDate.getMonth() &&
        current.getFullYear() === autoCancelDate.getFullYear()
      ) {
        // Solo contar hasta autoCancelDate
        hoursRemaining += (autoCancelDate.getTime() - current.getTime()) / (1000 * 60 * 60);
        break;
      } else {
        // Contar horas completas del día
        hoursRemaining += (dayEnd.getTime() - current.getTime()) / (1000 * 60 * 60);
        current = nextBusinessDayStart(current);
      }
    } else {
      // Avanzar al siguiente día hábil
      current = nextBusinessDayStart(current);
    }
  }

  return Math.max(0, hoursRemaining);
}

/**
 * Formatea el tiempo restante hasta la cancelación automática en un formato legible
 * @param createdAt - Fecha de creación del pago
 * @param seatCount - Número de asientos en la compra (opcional, por defecto usa 48 horas)
 */
export function formatTimeUntilAutoCancel(
  createdAt: string | Date,
  seatCount?: number
): {
  hours: number;
  days: number;
  formatted: string;
  isExpired: boolean;
  limitDays: number;
  limitHours: number;
} {
  const created = typeof createdAt === 'string' ? new Date(createdAt) : createdAt;
  const businessHoursLimit = seatCount !== undefined ? getBusinessHoursLimit(seatCount) : 48;
  const hoursRemaining = calculateBusinessHoursRemaining(created, businessHoursLimit);
  const autoCancelDate = calculateAutoCancelDate(created, businessHoursLimit);
  const now = getMXNNow();
  const isExpired = now >= autoCancelDate;

  const limitDays = businessHoursLimit === 45 ? 5 : businessHoursLimit === 27 ? 3 : 3;

  if (isExpired || hoursRemaining === 0) {
    return {
      hours: 0,
      days: 0,
      formatted: 'Tiempo agotado',
      isExpired: true,
      limitDays,
      limitHours: businessHoursLimit,
    };
  }

  // Calcular días hábiles restantes
  const daysRemaining = Math.floor(hoursRemaining / 9); // 9 horas hábiles por día
  const hoursInDay = hoursRemaining % 9;

  let formatted = '';
  if (daysRemaining > 0) {
    formatted = `${daysRemaining} día${daysRemaining !== 1 ? 's' : ''} hábil${daysRemaining !== 1 ? 'es' : ''}`;
    if (hoursInDay > 0) {
      formatted += ` y ${Math.ceil(hoursInDay)} hora${Math.ceil(hoursInDay) !== 1 ? 's' : ''}`;
    }
  } else {
    formatted = `${Math.ceil(hoursRemaining)} hora${Math.ceil(hoursRemaining) !== 1 ? 's' : ''} hábil${Math.ceil(hoursRemaining) !== 1 ? 'es' : ''}`;
  }

  return {
    hours: Math.ceil(hoursRemaining),
    days: daysRemaining,
    formatted,
    isExpired: false,
    limitDays,
    limitHours: businessHoursLimit,
  };
}

/**
 * Formatea el tiempo restante hasta un límite absoluto en horas.
 * No aplica horas hábiles; cuenta tiempo real desde createdAt.
 */
export function formatTimeUntilAbsoluteDeadline(
  createdAt: string | Date,
  deadlineHours: number
): {
  hours: number;
  days: number;
  formatted: string;
  isExpired: boolean;
  limitDays: number;
  limitHours: number;
} {
  const created = typeof createdAt === 'string' ? new Date(createdAt) : createdAt;
  const deadlineAt = new Date(created.getTime() + deadlineHours * 60 * 60 * 1000);
  const now = getMXNNow();
  const msRemaining = Math.max(0, deadlineAt.getTime() - now.getTime());
  const isExpired = msRemaining === 0;

  const hoursRemaining = Math.ceil(msRemaining / (1000 * 60 * 60));
  const daysRemaining = Math.floor(hoursRemaining / 24);
  const hoursInDay = hoursRemaining % 24;

  let formatted = '';
  if (isExpired) {
    formatted = 'Tiempo agotado';
  } else if (daysRemaining > 0) {
    formatted = `${daysRemaining} día${daysRemaining !== 1 ? 's' : ''}`;
    if (hoursInDay > 0) {
      formatted += ` y ${hoursInDay} hora${hoursInDay !== 1 ? 's' : ''}`;
    }
  } else {
    formatted = `${hoursRemaining} hora${hoursRemaining !== 1 ? 's' : ''}`;
  }

  return {
    hours: hoursRemaining,
    days: daysRemaining,
    formatted,
    isExpired,
    limitDays: Math.floor(deadlineHours / 24),
    limitHours: deadlineHours,
  };
}

/**
 * Formatea el tiempo restante hasta un límite en HORAS HÁBILES.
 * Respeta L-V, 8:00 AM - 5:00 PM.
 */
export function formatTimeUntilBusinessDeadline(
  createdAt: string | Date,
  businessHoursLimit: number
): {
  hours: number;
  days: number;
  formatted: string;
  isExpired: boolean;
  limitDays: number;
  limitHours: number;
} {
  const created = typeof createdAt === 'string' ? new Date(createdAt) : createdAt;
  const hoursRemaining = calculateBusinessHoursRemaining(created, businessHoursLimit);
  const deadlineDate = calculateAutoCancelDate(created, businessHoursLimit);
  const now = getMXNNow();
  const isExpired = now >= deadlineDate;

  const limitDays = Math.ceil(businessHoursLimit / (BUSINESS_HOURS_END - BUSINESS_HOURS_START));

  if (isExpired || hoursRemaining === 0) {
    return {
      hours: 0,
      days: 0,
      formatted: 'Tiempo agotado',
      isExpired: true,
      limitDays,
      limitHours: businessHoursLimit,
    };
  }

  // Calcular días hábiles restantes según rango laboral (9 horas si fuera 9-18, aquí 9 -> usamos diferencia horaria)
  const hoursPerDay = BUSINESS_HOURS_END - BUSINESS_HOURS_START; // 9 antes, ahora 9 también
  const daysRemaining = Math.floor(hoursRemaining / hoursPerDay);
  const hoursInDay = Math.ceil(hoursRemaining % hoursPerDay);

  let formatted = '';
  if (daysRemaining > 0) {
    formatted = `${daysRemaining} día${daysRemaining !== 1 ? 's' : ''} hábil${daysRemaining !== 1 ? 'es' : ''}`;
    if (hoursInDay > 0) {
      formatted += ` y ${hoursInDay} hora${hoursInDay !== 1 ? 's' : ''}`;
    }
  } else {
    formatted = `${hoursInDay} hora${hoursInDay !== 1 ? 's' : ''} hábil${hoursInDay !== 1 ? 'es' : ''}`;
  }

  return {
    hours: Math.ceil(hoursRemaining),
    days: daysRemaining,
    formatted,
    isExpired: false,
    limitDays,
    limitHours: businessHoursLimit,
  };
}
